import { Button } from '@crowdfpl/ui-web/button';
import { Label } from '@crowdfpl/ui-web/label';
import { Switch } from '@crowdfpl/ui-web/switch';
import { Loader2 } from 'lucide-react';
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from '@/hooks/data/notifications';

const CATEGORY_LABELS: Record<string, string> = {
  duels: 'New duels from other managers',
};

export function NotificationPreferencesForm({ idPrefix = 'notif-pref' }: { idPrefix?: string }) {
  const { data: preferences, isLoading, isError, refetch } = useNotificationPreferences();
  const updatePreferences = useUpdateNotificationPreferences();
  const isPending = updatePreferences.isPending;

  const masterEnabled = preferences?.notificationsEnabled ?? true;

  const handleMasterToggle = (checked: boolean) => {
    updatePreferences.mutate({ notificationsEnabled: checked });
  };

  const handleCategoryToggle = (category: string, checked: boolean) => {
    updatePreferences.mutate({ categories: [{ category, enabled: checked }] });
  };

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading...</p>;
  }

  if (isError) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-destructive">Failed to load preferences.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const categories = preferences?.categories ?? [];

  return (
    <div className="space-y-4" aria-busy={isPending}>
      {isPending ? (
        <p className="flex items-center gap-2 text-xs text-muted-foreground" role="status" aria-live="polite">
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
          Saving preferences…
        </p>
      ) : null}
      <div className="flex items-center justify-between gap-4">
        <Label htmlFor={`${idPrefix}-master`} className="cursor-pointer flex-1 text-sm font-medium">
          App notifications
        </Label>
        <Switch
          id={`${idPrefix}-master`}
          checked={masterEnabled}
          onCheckedChange={handleMasterToggle}
          disabled={isPending}
        />
      </div>

      <div className="border-t border-border pt-4">
        <p className="mb-3 text-xs text-muted-foreground">Notify me about</p>
        <div
          className={`space-y-4 transition-opacity ${!masterEnabled ? 'pointer-events-none opacity-50' : ''}`}
        >
          {categories.map((cat) => (
            <div key={cat.category} className="flex items-center justify-between gap-4">
              <Label
                htmlFor={`${idPrefix}-${cat.category}`}
                className="cursor-pointer flex-1 text-sm"
              >
                {CATEGORY_LABELS[cat.category] ?? cat.category}
              </Label>
              <Switch
                id={`${idPrefix}-${cat.category}`}
                checked={cat.enabled}
                onCheckedChange={(checked) => handleCategoryToggle(cat.category, !!checked)}
                disabled={isPending || !masterEnabled}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
